import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { AreaChart, DonutChart } from '@mantine/charts';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

// لوحة المبيعات المباشرة — today's numbers for owners/CFOs/accountants,
// per branch or all combined. Polls the snapshot endpoint every 15s;
// no manual refresh needed while the screen sits on a wall or a phone.

const REFRESH_MS = 15_000;

interface BranchDto {
  branchId: string;
  nameAr: string;
}

interface LiveSalesSnapshot {
  generatedAtUtc: string;
  orderCount: number;
  revenue: number;
  avgTicket: number;
  lastOrderAtUtc: string | null;
  hourly: { localHour: number; orderCount: number; revenue: number }[];
  topItems: { itemNameSnapshot: string; quantitySold: number; revenue: number }[];
  payments: { paymentMethodCodeSnapshot: string; paymentCount: number; amount: number }[];
  byBranch: { branchId: string; branchNameAr: string; orderCount: number; revenue: number }[];
}

const PAYMENT_COLORS = ['wheelsRed.6', 'teal.6', 'indigo.6', 'orange.6', 'grape.6', 'cyan.6'];

export function LiveSalesPage() {
  const { t } = useTranslation();
  const [branchId, setBranchId] = useState<string | null>(null);

  const { data: branches } = useQuery({
    queryKey: ['branches'] as const,
    queryFn: async () => (await apiClient.get<BranchDto[]>('/api/branches')).data,
  });

  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['live-sales', branchId] as const,
    queryFn: async () =>
      (
        await apiClient.get<LiveSalesSnapshot>('/api/reports/live-sales', {
          params: branchId ? { branchId } : {},
        })
      ).data,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: true,
  });

  const hourlySeries = (data?.hourly ?? []).map((h) => ({
    hour: `${h.localHour}:00`,
    [t('liveSales.revenue')]: h.revenue,
  }));

  const paymentSeries = (data?.payments ?? []).map((p, i) => ({
    name: p.paymentMethodCodeSnapshot,
    value: p.amount,
    color: PAYMENT_COLORS[i % PAYMENT_COLORS.length]!,
  }));

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.general')}
        title={t('liveSales.title')}
        subtitle={t('liveSales.subtitle')}
      />

      <Paper p="md">
        <Group justify="space-between" wrap="wrap">
          <Select
            value={branchId}
            onChange={setBranchId}
            data={[
              { value: '', label: t('liveSales.allBranches') },
              ...(branches ?? []).map((b) => ({ value: b.branchId, label: b.nameAr })),
            ]}
            placeholder={t('liveSales.allBranches')}
            clearable
            w={260}
          />
          <Group gap="xs">
            <Badge variant="dot" color="green" size="lg">
              {t('liveSales.live')}
            </Badge>
            {dataUpdatedAt > 0 && (
              <Text size="xs" c="dimmed" className="font-numeric">
                {new Intl.DateTimeFormat('ar-PS', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  timeZone: 'Asia/Jerusalem',
                  numberingSystem: 'latn',
                }).format(new Date(dataUpdatedAt))}
              </Text>
            )}
          </Group>
        </Group>
      </Paper>

      {isLoading || !data ? (
        <Group justify="center" py="xl">
          <Loader color="wheelsRed" />
        </Group>
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <Kpi label={t('liveSales.revenueToday')} value={money(data.revenue)} accent />
            <Kpi label={t('liveSales.orders')} value={String(data.orderCount)} />
            <Kpi label={t('liveSales.avgTicket')} value={money(data.avgTicket)} />
            <Kpi
              label={t('liveSales.lastOrder')}
              value={
                data.lastOrderAtUtc
                  ? new Intl.DateTimeFormat('ar-PS', {
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: 'Asia/Jerusalem',
                      numberingSystem: 'latn',
                    }).format(new Date(data.lastOrderAtUtc))
                  : '—'
              }
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, lg: 2 }}>
            <Paper p="md">
              <Text fw={600} mb="sm">
                {t('liveSales.hourly')}
              </Text>
              {hourlySeries.length === 0 ? (
                <Empty label={t('liveSales.noSalesYet')} />
              ) : (
                <AreaChart
                  h={240}
                  data={hourlySeries}
                  dataKey="hour"
                  series={[{ name: t('liveSales.revenue'), color: 'wheelsRed.6' }]}
                  curveType="monotone"
                  withGradient
                />
              )}
            </Paper>

            <Paper p="md">
              <Text fw={600} mb="sm">
                {t('liveSales.paymentMix')}
              </Text>
              {paymentSeries.length === 0 ? (
                <Empty label={t('liveSales.noSalesYet')} />
              ) : (
                <Group justify="center">
                  <DonutChart
                    data={paymentSeries}
                    thickness={26}
                    size={200}
                    withLabelsLine={false}
                    tooltipDataSource="segment"
                  />
                </Group>
              )}
            </Paper>
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, lg: 2 }}>
            <Paper p="md">
              <Text fw={600} mb="sm">
                {t('liveSales.topItems')}
              </Text>
              <Table verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('liveSales.item')}</Table.Th>
                    <Table.Th>{t('liveSales.qty')}</Table.Th>
                    <Table.Th>{t('liveSales.revenue')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.topItems.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Empty label={t('liveSales.noSalesYet')} />
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    data.topItems.map((item) => (
                      <Table.Tr key={item.itemNameSnapshot}>
                        <Table.Td>{item.itemNameSnapshot}</Table.Td>
                        <Table.Td className="font-numeric">{item.quantitySold}</Table.Td>
                        <Table.Td className="font-numeric">{money(item.revenue)}</Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Paper>

            <Paper p="md">
              <Text fw={600} mb="sm">
                {t('liveSales.byBranch')}
              </Text>
              <Table verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('liveSales.branch')}</Table.Th>
                    <Table.Th>{t('liveSales.orders')}</Table.Th>
                    <Table.Th>{t('liveSales.revenue')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.byBranch.map((b) => (
                    <Table.Tr
                      key={b.branchId}
                      bg={branchId === b.branchId ? 'var(--wheels-bg)' : undefined}
                    >
                      <Table.Td>{b.branchNameAr}</Table.Td>
                      <Table.Td className="font-numeric">{b.orderCount}</Table.Td>
                      <Table.Td className="font-numeric">{money(b.revenue)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <Paper p="md">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text
        size="28px"
        fw={700}
        mt={6}
        c={accent ? 'var(--wheels-accent)' : 'var(--wheels-ink-strong)'}
        className="font-numeric"
      >
        {value}
      </Text>
    </Paper>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <Text size="sm" c="dimmed" ta="center" py="lg">
      {label}
    </Text>
  );
}

function money(v: number): string {
  return new Intl.NumberFormat('ar-PS', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 2,
    numberingSystem: 'latn',
  }).format(v);
}
